import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { z } from "astro/zod";
import { LucideArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Image } from "./Image";
import { blogSchema } from "./blog-cron/blog.types";

export interface CarouselType {
  category: { name: string; href?: string; hrefName?: string };
  blogs: Pick<
    z.infer<typeof blogSchema>,
    "title" | "slug" | "unsplashImageData" | "createdAt"
  >[];
}

export const ScrollableCarousels = ({
  carousels,
}: {
  carousels: CarouselType[];
}) => (
  <>
    {carousels.map((carousel, i) => (
      <Carousel key={i} {...carousel} />
    ))}
  </>
);

const Carousel = ({ category, blogs }: CarouselType) => {
  const scrollDivRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(blogs.length > 4);

  const onLeftArrowClick = () => {
    const { current: scrollDiv } = scrollDivRef;
    if (!scrollDiv) {
      return;
    }

    scrollDiv.scrollBy({
      left: -(scrollDiv.clientWidth * 0.9),
      behavior: "smooth",
    });
  };

  const onRightArrowClick = () => {
    const { current: scrollDiv } = scrollDivRef;
    if (!scrollDiv) {
      return;
    }

    scrollDiv.scrollBy({
      left: scrollDiv.clientWidth * 0.9,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const { current: scrollDiv } = scrollDivRef;
    if (!scrollDiv) {
      return;
    }

    const onScroll = () => {
      setCanScrollLeft(scrollDiv.scrollLeft > 0);
      setCanScrollRight(
        scrollDiv.scrollLeft + scrollDiv.clientWidth < scrollDiv.scrollWidth,
      );
    };

    scrollDiv.addEventListener("scroll", onScroll);

    return () => {
      scrollDiv.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div key={category.name}>
      <div className="flex justify-between items-end px-4 md:pl-2 md:pr-0">
        <h2 className="text-3xl md:text-4xl font-extralight tracking-[-0.01em] line-clamp-1 capitalize leading-[2.5] md:leading-loose px-0">
          {category.name}
        </h2>
        {category.href && (
          <a
            className="text-[13px] md:text-sm font-medium transition-opacity hover:opacity-50 leading-[3.7] md:leading-[3.4] text-neutral-11 md:px-1.5 relative bottom-1.5 sm:bottom-0 whitespace-nowrap"
            href={category.href}
          >
            {category.hrefName || "View all"} {">"}
          </a>
        )}
      </div>
      <div className="relative">
        <div
          className="-ml-1 px-3.5 md:pl-0 flex gap-2.5 overflow-x-scroll scrollbar-hidden"
          data-test-id="section-trending"
          ref={scrollDivRef}
        >
          {blogs.map((blog) => (
            <a
              href={`/blog/${blog.slug}`}
              key={blog.slug}
              className="min-w-[52vw] md:min-w-fit md:h-[28rem] aspect-[2/3] md:aspect-[3/4] group relative overflow-hidden rounded-lg"
            >
              <Image
                className="object-cover h-full w-full saturate-[1.2] duration-200 group-hover:opacity-85 absolute rounded-lg inset-0"
                src={blog.unsplashImageData?.urls.regular}
                alt={blog.unsplashImageData?.alt_description || blog.title}
                loading="eager"
              />
              <div className="absolute inset-x-0 bottom-0 px-2 py-2 md:px-4 md:py-3 flex items-center z-10 gap-1 md:gap-[5px]">
                <div className="relative">
                  <p className="text-xs md:text-sm leading-relaxed md:leading-relaxed text-background/45">
                    {new Date(blog.createdAt).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                  <p className="md:text-xl pr-4 font-light text-background line-clamp-2">
                    {blog.title}
                  </p>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/35 to-black/90" />
            </a>
          ))}
        </div>
        <div
          className={cn(
            "absolute inset-y-0 left-0 flex-center w-10 transition-opacity ease-in duration-75 z-10",
            !canScrollLeft && "opacity-0",
          )}
        >
          <Button
            variant="wrapper"
            className="group scale-x-[-1]"
            onClick={onLeftArrowClick}
            aria-label="Previous items"
          >
            <LucideArrowRight className="aspect-[30/56] text-white w-5 md:w-6 lg:w-7 group-hover:opacity-85 duration-200" />
          </Button>
        </div>
        <div
          className={cn(
            "absolute inset-y-0 right-0 flex-center w-10 transition-opacity ease-in duration-75 z-10",
            !canScrollRight && "opacity-0",
          )}
        >
          <Button
            variant="wrapper"
            className="group"
            onClick={onRightArrowClick}
            aria-label="Next items"
          >
            <LucideArrowRight className="aspect-[30/56] text-white w-5 md:w-6 lg:w-7 group-hover:opacity-85 duration-200" />
          </Button>
        </div>
      </div>
    </div>
  );
};
